/**
 * Wartende und abgelehnte Änderungen.
 *
 * Bei einem Prüfdokument darf nichts stillschweigend verschwinden. Was der
 * Server abgelehnt hat, steht hier mit Begründung – der Prüfer entscheidet,
 * ob er es erneut versucht oder verwirft.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Leerhinweis } from '../bausteine/Hinweise'
import {
  bogenLesen,
  offeneAuftraege,
  wartendesFoto,
  type Auftrag,
} from '../offline/db'
import { abarbeiten, beobachten, verwerfen } from '../offline/warteschlange'

const ARTTEXT: Record<Auftrag['art'], string> = {
  befund: 'Beurteilung',
  zuruecksetzen: 'Beurteilung zurückgenommen',
  foto: 'Foto',
  abschluss: 'Abschluss der Prüfung',
  baustein: 'Verwendungszähler',
}

export function Warteschlange() {
  const [auftraege, setzeAuftraege] = useState<Auftrag[]>([])
  const [laeuft, setzeLaeuft] = useState(false)

  const laden = useCallback(async () => {
    setzeAuftraege(await offeneAuftraege())
  }, [])

  useEffect(() => {
    void laden()
    return beobachten((stand) => {
      setzeLaeuft(stand.laeuft)
      void laden()
    })
  }, [laden])

  const wartend = auftraege.filter((a) => !a.fehler)
  const abgelehnt = auftraege.filter((a) => a.fehler)

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">Warteschlange</h1>
        <button
          type="button"
          className="knopf-haupt ml-auto"
          onClick={() => void abarbeiten()}
          disabled={laeuft || wartend.length === 0}
        >
          {laeuft ? 'Sendet …' : 'Jetzt senden'}
        </button>
      </header>

      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">Wartet auf Übertragung ({wartend.length})</h2>
        {wartend.length === 0 ? (
          <Leerhinweis titel="Alles übertragen" text="Es liegt nichts mehr an." />
        ) : (
          <ul className="space-y-2">
            {wartend.map((a) => (
              <li key={a.id} className="karte flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-semibold">{ARTTEXT[a.art]}</div>
                  <div className="text-base text-slate-700">
                    {new Date(a.erstellt_am).toLocaleString('de-DE')}
                    {a.versuche > 0 && ` · ${a.versuche} Versuche`}
                  </div>
                </div>
                <Link
                  to={`/pruefungen/${a.pruefung_id}/bogen`}
                  className="knopf-still px-3 text-base"
                >
                  Bogen
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {abgelehnt.length > 0 && (
        <section>
          <h2 className="mb-2 text-xl font-bold text-stufe-3">
            Vom Server abgelehnt ({abgelehnt.length})
          </h2>
          <p className="mb-2 text-base text-slate-700">
            Erneutes Senden ändert daran nichts. Ein abgelehntes Foto liegt nur
            noch hier auf diesem Gerät – sichern Sie es, bevor Sie es verwerfen.
          </p>
          <ul className="space-y-2">
            {abgelehnt.map((a) => (
              <Abgelehnt key={a.id} auftrag={a} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}


/**
 * Ein abgelehnter Auftrag – mit allem, was nötig ist, um etwas damit zu tun.
 *
 * Vorher stand hier eine Fehlermeldung, ein Verweis auf den Bogen und
 * „Verwerfen". Das reichte an drei Stellen nicht:
 *
 *   - Der Verweis führte auf die Liste, nicht auf den Punkt. Bei dreißig
 *     Punkten war nicht zu erkennen, welcher gemeint war.
 *   - Der Punkt wurde nicht benannt. „Foto – Nicht gefunden" sagt nichts.
 *   - Vor allem: das Bild war nicht zu retten. Es steckt in der Ablage auf
 *     diesem Gerät, und „Verwerfen" hätte es endgültig gelöscht. Ein Foto
 *     vom Platz lässt sich nur auf dem Platz noch einmal machen.
 */
function Abgelehnt({ auftrag }: { auftrag: Auftrag }) {
  const [bild, setzeBild] = useState<string | null>(null)
  const [punkt, setzePunkt] = useState<string | null>(null)

  useEffect(() => {
    let verworfen = false
    let adresse: string | null = null

    void (async () => {
      // Das Bild aus der Ablage holen, damit man sieht, worum es geht.
      if (auftrag.foto_schluessel) {
        const foto = await wartendesFoto(auftrag.foto_schluessel)
        if (foto && !verworfen) {
          adresse = URL.createObjectURL(foto.blob)
          setzeBild(adresse)
        }
      }

      // Und den Namen des Punktes aus dem abgelegten Bogen.
      if (auftrag.befund_id) {
        const bogen = await bogenLesen(auftrag.pruefung_id)
        const geraet = bogen?.geraetebefunde.find((b) => b.id === auftrag.befund_id)
        const stelle = bogen?.platzbefunde.find((b) => b.id === auftrag.befund_id)
        if (!verworfen && geraet) setzePunkt(geraet.bezeichnung)
        // Beim Platzpunkt die Gruppe dazu: „Zaun" allein ist mehrdeutig,
        // „Einfriedung · Zaun" nicht.
        if (!verworfen && stelle) setzePunkt(`${stelle.gruppe} · ${stelle.bezeichnung}`)
      }
    })()

    return () => {
      verworfen = true
      // Sonst hält der Browser den Bildspeicher bis zum Neuladen fest.
      if (adresse) URL.revokeObjectURL(adresse)
    }
  }, [auftrag.foto_schluessel, auftrag.befund_id, auftrag.pruefung_id])

  const ziel = auftrag.befund_id
    ? `/pruefungen/${auftrag.pruefung_id}/bogen?punkt=${auftrag.befund_id}`
    : `/pruefungen/${auftrag.pruefung_id}/bogen`

  return (
    <li className="karte border-stufe-3">
      <div className="font-semibold">
        {ARTTEXT[auftrag.art]}
        {punkt && <span className="font-normal text-slate-700"> · {punkt}</span>}
      </div>
      <p className="text-base text-stufe-3">{auftrag.fehler}</p>

      {bild && (
        <img
          src={bild}
          alt="Abgelehntes Foto"
          className="mt-2 max-h-48 rounded-feld border-2 border-slate-200 object-contain"
        />
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {bild && (
          /* Herunterladen statt nur anzeigen: danach liegt das Bild in der
             Fotoablage des Geräts und überlebt das Verwerfen. */
          <a
            href={bild}
            download={`spuer-foto-${auftrag.id}.jpg`}
            className="knopf-haupt px-3 text-base"
          >
            Foto sichern
          </a>
        )}
        <Link to={ziel} className="knopf-zweit px-3 text-base">
          {auftrag.befund_id ? 'Punkt im Bogen öffnen' : 'Im Bogen ansehen'}
        </Link>
        <button
          type="button"
          className="knopf-warnung px-3 text-base"
          onClick={() => {
            const frage = bild
              ? 'Das Foto ist nur auf diesem Gerät gespeichert und wird endgültig gelöscht. Vorher gesichert?'
              : 'Diese Änderung endgültig verwerfen?'
            if (window.confirm(frage)) {
              void verwerfen(auftrag.id!, auftrag.foto_schluessel)
            }
          }}
        >
          Verwerfen
        </button>
      </div>
    </li>
  )
}
